import React, { useState, useEffect } from 'react';
import { Link, useParams, Navigate } from 'react-router-dom';
import axios from 'axios';
import ReactMarkdown from 'react-markdown';
import DOMPurify from 'dompurify';
import { Header, Footer } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { FileText, Search, Loader2, ArrowLeft, Calendar, User, Clock, Tag, ImageIcon, Sparkles, Building2, CheckCircle, Star, ClipboardList, ChevronDown } from 'lucide-react';
import { API } from '../utils/api';
import AuthorBox from '../components/AuthorBox';
import {
  getAuthorByCategory,
  getAuthorByName,
  buildAuthorPersonSchema,
} from '../config/authors';


function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  return d.toLocaleDateString('es-CL', { day: 'numeric', month: 'long', year: 'numeric' });
}

// Default blog images by category for variety
const BLOG_IMAGES = {
  'alcoholismo': 'https://images.unsplash.com/photo-1569924256460-c6a7d6e8e4f8?w=800&h=400&fit=crop',
  'drogas': 'https://images.unsplash.com/photo-1559757148-5c350d0d3c56?w=800&h=400&fit=crop',
  'tratamiento': 'https://images.unsplash.com/photo-1576091160550-2173dba999ef?w=800&h=400&fit=crop',
  'recuperacion': 'https://images.unsplash.com/photo-1544027993-37dbfe43562a?w=800&h=400&fit=crop',
  'familia': 'https://images.unsplash.com/photo-1511895426328-dc8714191300?w=800&h=400&fit=crop',
  'salud-mental': 'https://images.unsplash.com/photo-1493836512294-502baa1986e2?w=800&h=400&fit=crop',
  'prevencion': 'https://images.unsplash.com/photo-1506126613408-eca07ce68773?w=800&h=400&fit=crop',
  'testimonios': 'https://images.unsplash.com/photo-1517486808906-6ca8b3f04846?w=800&h=400&fit=crop',
  'general': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=400&fit=crop',
  'default': 'https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=800&h=400&fit=crop'
};

// Function to get unique image for article based on category or id
function getArticleImage(article) {
  if (article.image_url || article.cover_image || article.featured_image) {
    return article.image_url || article.cover_image || article.featured_image;
  }
  
  // Try to match category
  const category = (article.category || '').toLowerCase();
  for (const [key, url] of Object.entries(BLOG_IMAGES)) {
    if (category.includes(key)) {
      return url;
    }
  }
  
  // Use article id hash to select a consistent but varied image
  const imageKeys = Object.keys(BLOG_IMAGES).filter(k => k !== 'default');
  const hash = (article.article_id || article.title || '').split('').reduce((a, b) => {
    a = ((a << 5) - a) + b.charCodeAt(0);
    return a & a;
  }, 0);
  const index = Math.abs(hash) % imageKeys.length;
  return BLOG_IMAGES[imageKeys[index]];
}

const DEFAULT_BLOG_IMAGE = BLOG_IMAGES.default;

export function BlogPage() {
  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pages, setPages] = useState(1);

  useEffect(function() {
    async function load() {
      setLoading(true);
      try {
        const url = API + '/articles/public?page=' + currentPage + '&limit=10' + (searchTerm ? '&search=' + searchTerm : '');
        const res = await axios.get(url);
        setArticles(res.data.articles || []);
        setPages(res.data.pages || 1);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [currentPage, searchTerm]);

  function onSearch(e) {
    e.preventDefault();
    setCurrentPage(1);
  }

  // Schema for Blog listing
  const blogListSchema = {
    "@context": "https://schema.org",
    "@type": "Blog",
    "name": "Blog Sin Adicciones",
    "description": "Artículos sobre recuperación de adicciones, tratamientos y salud mental en Chile",
    "url": "https://sinadicciones.org/blog",
    "publisher": {
      "@type": "Organization",
      "name": "Sin Adicciones Chile",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sinadicciones.org/logo.png"
      }
    }
  };

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>Blog sobre Adicciones y Recuperación | Sin Adicciones Chile</title>
        <meta name="description" content="Artículos informativos sobre recuperación de adicciones, tratamientos, salud mental y bienestar. Recursos para pacientes y familiares en Chile." />
        <meta property="og:title" content="Blog sobre Adicciones y Recuperación | Sin Adicciones Chile" />
        <meta property="og:description" content="Artículos informativos sobre recuperación de adicciones, tratamientos, salud mental y bienestar." />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://sinadicciones.org/blog" />
        <meta property="og:image" content="https://sinadicciones.org/og-image.png" />
        <meta property="og:image:width" content="1280" />
        <meta property="og:image:height" content="896" />
        <meta property="og:site_name" content="SinAdicciones.org" />
        <meta property="og:locale" content="es_CL" />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Blog sobre Adicciones y Recuperación" />
        <meta name="twitter:description" content="Recursos informativos sobre adicciones y recuperación en Chile" />
        <meta name="twitter:image" content="https://sinadicciones.org/og-image.png" />
        <link rel="canonical" href="https://sinadicciones.org/blog" />
        <script type="application/ld+json">{JSON.stringify(blogListSchema)}</script>
      
      <Header />
      <main className="pt-24 pb-16">
        <section className="bg-slate-900 border-b border-slate-800 py-16">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <h1 className="text-4xl md:text-5xl font-bold text-slate-50 mb-4" data-testid="blog-title">Blog</h1>
            <p className="text-lg text-slate-400">Artículos sobre recuperación y salud mental</p>
          </div>
        </section>
        <section className="py-8 border-b border-slate-800">
          <div className="max-w-7xl mx-auto px-4 md:px-8">
            <form onSubmit={onSearch} className="flex gap-4 max-w-xl">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-500" />
                <Input 
                  placeholder="Buscar artículos..." 
                  value={searchTerm} 
                  onChange={function(e) { setSearchTerm(e.target.value); }} 
                  className="pl-10 bg-slate-950 border-slate-700 text-slate-200 placeholder:text-slate-500 focus:border-emerald-500 focus:ring-emerald-500/20" 
                />
              </div>
              <Button type="submit" className="bg-emerald-500 hover:bg-emerald-600 rounded-lg">Buscar</Button>
            </form>
          </div>
        </section>
        <section className="py-12">
          <div className="max-w-4xl mx-auto px-4 md:px-8">
            {loading ? (
              <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-emerald-500" /></div>
            ) : articles.length === 0 ? (
              <div className="text-center py-20 bg-slate-900 rounded-xl border border-slate-800">
                <FileText className="w-16 h-16 text-slate-700 mx-auto mb-4" />
                <h3 className="text-xl text-slate-50 mb-2">No hay artículos</h3>
                <p className="text-slate-400">Pronto publicaremos contenido nuevo</p>
              </div>
            ) : (
              <div className="space-y-8">
                {articles.map(function(a) {
                  const imageUrl = getArticleImage(a);
                  return (
                    <Link key={a.article_id} to={'/blog/' + a.slug} className="block">
                      <Card className="bg-slate-900 border-slate-800 hover:border-emerald-500/50 hover:shadow-lg hover:shadow-emerald-500/5 transition-all duration-300 overflow-hidden group">
                        <div className="flex flex-col md:flex-row">
                          {/* Image */}
                          <div className="md:w-72 h-48 md:h-auto flex-shrink-0 relative overflow-hidden bg-slate-800">
                            <img 
                              src={imageUrl} 
                              alt={a.title}
                              className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                              onError={(e) => { e.target.src = DEFAULT_BLOG_IMAGE; }}
                            />
                            {a.category && (
                              <span className="absolute top-3 left-3 px-2 py-1 bg-emerald-500 text-white text-xs font-medium rounded shadow">
                                {a.category}
                              </span>
                            )}
                          </div>
                          {/* Content */}
                          <CardContent className="p-6 flex-1">
                            <div className="flex items-center gap-4 text-sm text-slate-500 mb-3">
                              <span className="flex items-center gap-1">
                                <Calendar className="w-4 h-4" />
                                {formatDate(a.created_at)}
                              </span>
                              {a.author_name && (
                                <span className="flex items-center gap-1">
                                  <User className="w-4 h-4" />
                                  {a.author_name}
                                </span>
                              )}
                              {a.reading_time && (
                                <span className="flex items-center gap-1">
                                  <Clock className="w-4 h-4" />
                                  {a.reading_time} min
                                </span>
                              )}
                            </div>
                            <h2 className="text-xl font-semibold text-slate-100 group-hover:text-emerald-400 transition-colors duration-200 mb-3 line-clamp-2">
                              {a.title}
                            </h2>
                            {a.excerpt && (
                              <p className="text-slate-400 line-clamp-2 text-sm">
                                {a.excerpt}
                              </p>
                            )}
                            <div className="mt-4">
                              <span className="text-emerald-400 text-sm font-medium group-hover:underline">
                                Leer más →
                              </span>
                            </div>
                          </CardContent>
                        </div>
                      </Card>
                    </Link>
                  );
                })}
              </div>
            )}
            {pages > 1 && (
              <div className="flex justify-center gap-2 mt-12">
                <Button variant="outline" onClick={function() { setCurrentPage(function(p) { return Math.max(1, p - 1); }); }} disabled={currentPage === 1} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Anterior</Button>
                <span className="text-sm text-slate-400 px-4 flex items-center">Página {currentPage} de {pages}</span>
                <Button variant="outline" onClick={function() { setCurrentPage(function(p) { return Math.min(pages, p + 1); }); }} disabled={currentPage === pages} className="border-slate-700 text-slate-300 hover:bg-slate-800 disabled:opacity-50">Siguiente</Button>
              </div>
            )}
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}

// ── 301-equivalent redirects de slugs antiguos a nuevos ──
// El SPA no puede emitir un 301 puro, pero `<Navigate replace>` + `<meta http-equiv="refresh">`
// + canonical en destino permiten que Googlebot trate la URL antigua como redirigida.
const LEGACY_SLUG_REDIRECTS = {
  'el-mapa-invertido-como-entender-tu-adiccion-y-empezar-el-reto-21-dias-sin-drogas':
    'el-mapa-inverso-cómo-entender-tu-adicción-y-empezar-el-reto-21-días-sin-drogas',
};

export function ArticleDetail() {
  const params = useParams();
  const slug = params.slug;
  const redirectSlug = slug ? LEGACY_SLUG_REDIRECTS[slug] : null;

  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);
  const [featuredCenters, setFeaturedCenters] = useState([]);
  const [toolsOpen, setToolsOpen] = useState(true);

  useEffect(function() {
    async function load() {
      try {
        const [articleRes, centersRes] = await Promise.all([
          axios.get(API + '/articles/public/' + slug),
          axios.get(API + '/centers/featured?limit=3')
        ]);
        setArticle(articleRes.data);
        setFeaturedCenters(centersRes.data.centers || []);
      } catch (e) {
        console.error(e);
      }
      setLoading(false);
    }
    load();
  }, [slug]);

  // Redirect legacy slugs after all hooks have been declared
  if (redirectSlug) {
    const target = '/blog/' + redirectSlug;
    return (
      <>
        <title>Redirigiendo… | SinAdicciones.org</title>
        <meta httpEquiv="refresh" content={'0; url=' + target} />
        <link rel="canonical" href={'https://sinadicciones.org' + target} />
        <meta name="robots" content="noindex, follow" />
        <Navigate to={target} replace />
      </>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950">
        <Header />
        <main className="pt-24 pb-16 flex justify-center items-center min-h-[60vh]">
          <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
        </main>
        <Footer />
      </div>
    );
  }

  if (!article) {
    return (
      <div className="min-h-screen bg-slate-950">
        <title>Artículo no encontrado (404) | SinAdicciones.org</title>
        <meta name="robots" content="noindex, follow" />
        <meta name="description" content="El artículo que buscas no existe o ha sido removido del blog." />
        <Header />
        <main className="pt-24 pb-16">
          <div className="max-w-4xl mx-auto px-4 text-center py-20">
            <h1 className="text-3xl font-bold text-slate-50 mb-4">Artículo no encontrado</h1>
            <Link to="/blog">
              <Button className="bg-emerald-500 hover:bg-emerald-600">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al blog
              </Button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const articleUrl = window.location.origin + '/blog/' + article.slug;
  const imageUrl = getArticleImage(article);

  // Resolve canonical author: by category preferred, fallback by name in article
  const articleAuthor = getAuthorByCategory(article.category) || getAuthorByName(article.author_name);

  // Schema for Article (SEO optimized) — Person author with sameAs LinkedIn = strong E-E-A-T
  const articleSchema = {
    "@context": "https://schema.org",
    "@type": "Article",
    "headline": article.title,
    "description": article.meta_description || article.excerpt || article.title,
    "image": imageUrl,
    "author": articleAuthor ? buildAuthorPersonSchema(articleAuthor) : {
      "@type": "Organization",
      "name": article.author_name || "Sin Adicciones",
      "url": "https://sinadicciones.org"
    },
    "publisher": {
      "@type": "Organization",
      "name": "Sin Adicciones Chile",
      "logo": {
        "@type": "ImageObject",
        "url": "https://sinadicciones.org/logo.png"
      }
    },
    "datePublished": article.created_at,
    "dateModified": article.updated_at || article.created_at,
    "mainEntityOfPage": {
      "@type": "WebPage",
      "@id": articleUrl
    },
    "keywords": article.meta_keywords || (article.tags ? article.tags.join(', ') : ''),
    "articleSection": article.category || "Salud Mental",
    "wordCount": article.content ? article.content.replace(/<[^>]*>/g, '').split(/\s+/).length : 0,
    "inLanguage": "es-CL"
  };

  const tools = [
    { name: 'Test Alcoholismo (AUDIT)', href: '/herramientas/test/audit', icon: '🍷' },
    { name: 'Test Drogas (DAST)', href: '/herramientas/test/dast', icon: '💊' },
    { name: 'Test Ludopatía (PGSI)', href: '/herramientas/test/pgsi', icon: '🎰' },
    { name: 'Test Cambio (URICA)', href: '/herramientas/test/urica', icon: '🔄' },
    { name: 'Test Codependencia', href: '/herramientas/test/codependencia', icon: '👥' },
    { name: 'Calculadora de Costos', href: '/calculadora', icon: '💰' },
  ];

  return (
    <div className="min-h-screen bg-slate-950">
      
        <title>{article.title + ' | Blog Sin Adicciones'}</title>
        <meta name="description" content={article.meta_description || article.excerpt || article.title} />
        <meta name="keywords" content={article.meta_keywords || (article.tags ? article.tags.join(', ') : '')} />
        <meta property="og:title" content={article.title} />
        <meta property="og:description" content={article.meta_description || article.excerpt || article.title} />
        <meta property="og:type" content="article" />
        <meta property="og:url" content={articleUrl} />
        <meta property="og:image" content={imageUrl} />
        <meta property="article:published_time" content={article.created_at} />
        <meta name="author" content={articleAuthor?.name || article.author_name || "Sin Adicciones"} />
        <meta property="article:author" content={articleAuthor ? `https://sinadicciones.org/autor/${articleAuthor.slug}` : (article.author_name || "Sin Adicciones")} />
        {article.category && <meta property="article:section" content={article.category} />}
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={article.title} />
        <meta name="twitter:description" content={article.meta_description || article.excerpt || article.title} />
        <meta name="twitter:image" content={imageUrl} />
        <link rel="canonical" href={articleUrl} />
        <script type="application/ld+json">{JSON.stringify(articleSchema)}</script>
      
      <Header />
      <main className="pt-24 pb-16">
        <div className="max-w-7xl mx-auto px-4 md:px-8">
          <div className="flex flex-col lg:flex-row gap-8">
            {/* Main Content */}
            <article className="flex-1 max-w-4xl">
              {/* Back Button */}
              <Link to="/blog" className="inline-flex items-center text-slate-400 hover:text-emerald-400 mb-8 transition-colors duration-200 font-sans">
                <ArrowLeft className="w-4 h-4 mr-2" />
                Volver al blog
              </Link>
              
              {/* Featured Image */}
              {imageUrl && (
                <div className="mb-8 rounded-xl overflow-hidden">
                  <img 
                    src={imageUrl} 
                    alt={article.title}
                    className="w-full h-64 md:h-96 object-cover"
                    onError={(e) => { e.target.style.display = 'none'; }}
                  />
                </div>
              )}
              
              {/* Category Badge */}
              {article.category && (
                <span className="inline-flex items-center gap-1 px-3 py-1 bg-emerald-500/10 text-emerald-400 text-sm font-medium rounded-full mb-4 font-sans">
                  <Tag className="w-3 h-3" />
                  {article.category}
                </span>
              )}
              
              {/* Title */}
              <h1 className="text-3xl md:text-4xl font-bold text-slate-50 mb-6 font-sans">{article.title}</h1>
              
              {/* Meta */}
              <div className="flex flex-wrap items-center gap-4 text-sm text-slate-400 mb-8 pb-8 border-b border-slate-800 font-sans">
                {articleAuthor ? (
                  <Link
                    to={`/autor/${articleAuthor.slug}`}
                    className="flex items-center gap-2 hover:text-emerald-400 transition-colors group"
                    data-testid="article-author-link"
                  >
                    <img
                      src={articleAuthor.image}
                      alt={articleAuthor.name}
                      className="w-7 h-7 rounded-full object-cover border border-slate-700 group-hover:border-emerald-500/50 transition-colors"
                    />
                    <span className="font-medium">{articleAuthor.name}</span>
                  </Link>
                ) : (article.author_name || article.author) && (
                  <span className="flex items-center gap-2">
                    <User className="w-4 h-4" />
                    {article.author_name || article.author}
                  </span>
                )}
                <span className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" />
                  {formatDate(article.created_at)}
                </span>
                {(article.reading_time || article.read_time) && (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    {article.reading_time || article.read_time} min de lectura
                  </span>
                )}
              </div>
              
              {/* Content - HTML or Markdown */}
              <div className="bg-slate-900 rounded-xl p-6 md:p-10 border border-slate-800 prose prose-invert max-w-none font-sans
                prose-headings:text-slate-50 prose-headings:font-bold prose-headings:font-sans
                prose-h1:text-3xl prose-h1:mt-10 prose-h1:mb-6
                prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4 prose-h2:text-emerald-400
                prose-h3:text-xl prose-h3:mt-8 prose-h3:mb-3
                prose-h4:text-lg prose-h4:mt-6 prose-h4:mb-2
                prose-p:text-slate-300 prose-p:leading-relaxed prose-p:mb-4 prose-p:font-sans
                prose-li:text-slate-300 prose-li:my-1 prose-li:font-sans
                prose-ul:my-4 prose-ol:my-4
                prose-strong:text-slate-50 prose-strong:font-semibold
                prose-blockquote:border-l-4 prose-blockquote:border-emerald-500 prose-blockquote:bg-emerald-500/10 prose-blockquote:py-3 prose-blockquote:px-4 prose-blockquote:rounded-r-lg prose-blockquote:not-italic prose-blockquote:my-6
                prose-a:text-emerald-400 prose-a:font-medium prose-a:underline prose-a:underline-offset-2 hover:prose-a:text-emerald-300
                prose-code:text-emerald-400 prose-code:bg-emerald-500/10 prose-code:px-1 prose-code:rounded
                prose-img:rounded-xl prose-img:my-8
                prose-table:border-collapse prose-th:bg-slate-800 prose-th:p-3 prose-th:text-slate-200 prose-td:p-3 prose-td:border-slate-700 prose-td:text-slate-300
              ">
                {/* Detect HTML vs Markdown - if content starts with < it's HTML */}
                {article.content && article.content.trim().startsWith('<') ? (
                  <div dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(article.content || '', { ADD_ATTR: ['target', 'rel'] }) }} />
                ) : (
                  <ReactMarkdown>{article.content}</ReactMarkdown>
                )}
              </div>

              {/* Featured Centers Section */}
              {featuredCenters.length > 0 && (
                <div className="mt-12 pt-8 border-t border-slate-800">
                  <h3 className="text-xl font-bold text-slate-50 mb-6 font-sans flex items-center gap-2">
                    <Star className="w-5 h-5 text-amber-400" />
                    Centros Destacados
                  </h3>
                  <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {featuredCenters.map(function(center) {
                      return (
                        <Link 
                          key={center.center_id} 
                          to={'/centros/' + (center.slug || center.center_id)}
                          className="group block"
                        >
                          <Card className="bg-gradient-to-br from-amber-500/5 to-amber-600/10 border-amber-500/20 hover:border-amber-500/40 transition-all overflow-hidden">
                            <CardContent className="p-4">
                              <div className="flex items-center gap-3">
                                {center.logo ? (
                                  <img src={center.logo} alt={center.name} className="w-12 h-12 rounded-lg object-cover" />
                                ) : (
                                  <div className="w-12 h-12 rounded-lg bg-slate-800 flex items-center justify-center">
                                    <Building2 className="w-6 h-6 text-slate-600" />
                                  </div>
                                )}
                                <div className="flex-1 min-w-0">
                                  <p className="font-medium text-slate-50 truncate group-hover:text-amber-400 transition-colors font-sans flex items-center gap-1">
                                    {center.name}
                                    {center.is_verified && <CheckCircle className="w-4 h-4 text-emerald-400 flex-shrink-0" />}
                                  </p>
                                  <p className="text-sm text-slate-400 font-sans">{center.city}</p>
                                </div>
                              </div>
                            </CardContent>
                          </Card>
                        </Link>
                      );
                    })}
                  </div>
                  <div className="mt-4 text-center">
                    <Link to="/centros-de-rehabilitacion-drogas-chile" className="text-emerald-400 hover:text-emerald-300 text-sm font-medium font-sans underline underline-offset-2">
                      Ver todos los centros →
                    </Link>
                  </div>
                </div>
              )}
              
              {/* Author Box (E-E-A-T) */}
              <AuthorBox author={articleAuthor} />

              {/* Share Section */}
              <div className="mt-12 pt-8 border-t border-slate-800">
                <p className="text-slate-400 text-sm mb-4 font-sans">Comparte este artículo:</p>
                <div className="flex gap-3">
                  <a 
                    href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(articleUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-sm font-medium rounded-lg transition-colors font-sans"
                  >
                    Facebook
                  </a>
                  <a 
                    href={`https://twitter.com/intent/tweet?url=${encodeURIComponent(articleUrl)}&text=${encodeURIComponent(article.title)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-sky-500 hover:bg-sky-600 text-white text-sm font-medium rounded-lg transition-colors font-sans"
                  >
                    Twitter
                  </a>
                  <a 
                    href={`https://wa.me/?text=${encodeURIComponent(article.title + ' ' + articleUrl)}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="px-4 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-lg transition-colors font-sans"
                  >
                    WhatsApp
                  </a>
                </div>
              </div>
            </article>

            {/* Sidebar */}
            <aside className="lg:w-80 space-y-6">
              {/* Asesoría Banner */}
              <Card className="bg-gradient-to-br from-emerald-500/20 to-teal-600/20 border-emerald-500/30 overflow-hidden">
                <CardContent className="p-6 text-center">
                  <div className="w-14 h-14 rounded-full bg-emerald-500/20 flex items-center justify-center mx-auto mb-4">
                    <Sparkles className="w-7 h-7 text-emerald-400" />
                  </div>
                  <h3 className="text-lg font-bold text-slate-50 mb-2 font-sans">¿Necesitas ayuda?</h3>
                  <p className="text-slate-300 text-sm mb-4 font-sans">
                    Solicita una asesoría experta personalizada
                  </p>
                  <Link to="/asesoria">
                    <Button className="w-full bg-emerald-500 hover:bg-emerald-600 font-sans">
                      Solicitar Asesoría
                    </Button>
                  </Link>
                </CardContent>
              </Card>

              {/* Tools Sidebar */}
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <button 
                    onClick={() => setToolsOpen(!toolsOpen)}
                    className="flex items-center justify-between w-full text-left mb-3"
                  >
                    <h3 className="text-base font-bold text-slate-50 font-sans flex items-center gap-2">
                      <ClipboardList className="w-5 h-5 text-emerald-400" />
                      Herramientas
                    </h3>
                    <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${toolsOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {toolsOpen && (
                    <div className="space-y-2">
                      {tools.map(function(tool, idx) {
                        return (
                          <Link 
                            key={idx}
                            to={tool.href}
                            className="flex items-center gap-3 p-2 rounded-lg hover:bg-slate-800 transition-colors group"
                          >
                            <span className="text-lg">{tool.icon}</span>
                            <span className="text-sm text-slate-300 group-hover:text-emerald-400 transition-colors font-sans">{tool.name}</span>
                          </Link>
                        );
                      })}
                    </div>
                  )}
                </CardContent>
              </Card>

              {/* Quick Links */}
              <Card className="bg-slate-900 border-slate-800">
                <CardContent className="p-4">
                  <h3 className="text-base font-bold text-slate-50 mb-3 font-sans">Enlaces Rápidos</h3>
                  <div className="space-y-2">
                    <Link to="/centros-de-rehabilitacion-drogas-chile" className="flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-400 transition-colors font-sans">
                      <Building2 className="w-4 h-4" />
                      Buscar Centros
                    </Link>
                    <Link to="/profesionales" className="flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-400 transition-colors font-sans">
                      <User className="w-4 h-4" />
                      Buscar Profesionales
                    </Link>
                    <Link to="/blog" className="flex items-center gap-2 text-sm text-slate-300 hover:text-emerald-400 transition-colors font-sans">
                      <FileText className="w-4 h-4" />
                      Más Artículos
                    </Link>
                  </div>
                </CardContent>
              </Card>
            </aside>
          </div>
        </div>
      </main>
      <Footer />
    </div>
  );
}
